import React from 'react';
import PropTypes from 'prop-types';
import { PageError } from '@/errors/ErrorViews';
import { captureBoundaryError } from '@/services/telemetry';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    captureBoundaryError(error, info, this.props.boundaryName);
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  componentDidUpdate(prevProps) {
    const { resetKeys } = this.props;
    if (this.state.error && resetKeys && prevProps.resetKeys) {
      const hasReset = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys[index],
      );
      if (hasReset) {
        this.resetError();
      }
    }
  }

  resetError = () => {
    this.setState({ error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;
    if (error) {
      if (fallback) {
        return fallback({ error, reset: this.resetError });
      }
      return (
        <PageError
          title="The app hit an unexpected error"
          message="Try reloading. If it keeps happening, please contact support."
          onPrimaryAction={() => window.location.reload()}
        />
      );
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.func,
  onError: PropTypes.func,
  onReset: PropTypes.func,
  resetKeys: PropTypes.array,
  boundaryName: PropTypes.string,
};

export default ErrorBoundary;
